/* eslint-disable consistent-return */
import classNames from 'classnames';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { getKeyboardFocusableElements } from 'utilities/getKeyboardFocusableElements';
import styles from './TableDropdown.module.scss';

export interface TableDropdownProps {
	className?: string;
	title: string;
	id: string;
	options: string[];
	onSelect: (v: string) => unknown;
	theme?: 'light';
	disabled?: boolean;
	filterActive?: boolean;
	filterAriaLabel?: string;
}
// TODO combine with Dropdown when we have time, they work differently but look the same.
export const TableDropdown: React.FC<TableDropdownProps> = ({
	className,
	title,
	id,
	options,
	onSelect,
	theme,
	disabled = false,
	filterActive = false,
	filterAriaLabel = 'Søg',
}: TableDropdownProps) => {
	const dropdown = useRef<HTMLDivElement>(null);
	const dropdownMenu = useRef<HTMLDivElement>(null);

	const [isOpen, setIsOpen] = useState(false);
	const [filterValue, setFilterValue] = useState('');

	options = options?.filter((item) => item.toLowerCase().includes(filterValue.toLowerCase()));
	const toggle = (event) => {
		if (disabled) return;
		event.stopPropagation();
		if (isOpen) {
			dropdown.current?.focus();
			setIsOpen(false);
			return;
		}
		setIsOpen(true);
	};

	const handleEventOptionKeystroke = (e: React.KeyboardEvent<HTMLDivElement>, option: string) => {
		if (e.key === 'Escape') {
			toggle(e);
		}
		if (e.key === 'Enter') {
			e.preventDefault();
			onSelect(option);
			toggle(e);
		}
	};

	useEffect(() => {
		const checkOutsideContainerClick = (e: MouseEvent) => {
			if (isOpen && dropdown.current && !dropdown.current.contains(e.target as HTMLElement)) {
				setIsOpen(false);
			}
		};
		document.body.addEventListener('click', checkOutsideContainerClick, {
			once: true,
			capture: true,
		});

		return () => {
			document.body.removeEventListener('click', checkOutsideContainerClick);
		};
	}, [isOpen]);

	useEffect(() => dropdown.current?.focus(), [isOpen]);
	useEffect(() => {
		if (!dropdownMenu.current) return;

		const focusables = getKeyboardFocusableElements(dropdownMenu.current);
		if (focusables.length === 0) return;
		const first = focusables[0];
		const last = focusables[focusables.length - 1];
		const handleEventFirstElement = (e: KeyboardEvent) => {
			if (e.code === 'Tab' && e.shiftKey) {
				e.preventDefault();
				last.focus();
			}
		};
		const handleEventLastElement = (e: KeyboardEvent) => {
			if (e.code === 'Tab' && !e.shiftKey) {
				e.preventDefault();
				first.focus();
			}
		};

		if (isOpen) {
			first.addEventListener('keydown', handleEventFirstElement);
			last.addEventListener('keydown', handleEventLastElement);
		}
		return () => {
			first.removeEventListener('keydown', handleEventFirstElement);
			last.removeEventListener('keydown', handleEventLastElement);
		};
	}, [isOpen]);

	return (
		<div className={classNames(styles.TableDropdown, styles[`TableDropdown___${theme}`], className)} ref={dropdown}>
			<div className={styles.TableDropdown_wrapper}>
				<button
					aria-controls={id + 'listbox'}
					aria-expanded={isOpen}
					aria-haspopup="listbox"
					aria-label={title}
					className={classNames(styles.TableDropdown_input, disabled ? styles.TableDropdown_input___disabled : '')}
					onClick={(e) => toggle(e)}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							toggle(e);
						}
					}}
					role="combobox"
					tabIndex={0}
					id={id}
					disabled={disabled}
				>
					{title}
				</button>

				{filterActive && (
					<div className={classNames(styles.TableDropdown_background, { [styles.TableDropdown_menu___hidden]: !isOpen })}>
						<input
							className={classNames(styles.TableDropdown_filter)}
							type="search"
							value={filterValue}
							onChange={(e) => setFilterValue(e.target.value.toLowerCase())}
							aria-label={filterAriaLabel}
						/>
					</div>
				)}
				<div
					className={classNames(
						styles.TableDropdown_menu,
						{ [styles.TableDropdown_menu___hidden]: !isOpen },
						{ [styles.TableDropdown_verticalSpace]: filterActive },
					)}
					ref={dropdownMenu}
					role="listbox"
					id={id + 'listbox'}
					aria-labelledby={id}
					tabIndex={-1}
				>
					{options?.map((option, index) => {
						return (
							<div
								className={styles.TableDropdown_option}
								role="option"
								key={index}
								tabIndex={0}
								onClick={(e) => {
									onSelect(option);
									toggle(e);
									setFilterValue('');
								}}
								onKeyDown={(e) => {
									handleEventOptionKeystroke(e, option);
								}}
							>
								{option}
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};
